/**
 * Утилиты для работы с роутингом и парсингом URL
 */

/** Языковой префикс в пути: /ru/... или /en/... */
export const LANG_PREFIX_REGEX = /^\/(ru|en)(\/.*)?$/;

/**
 * Возвращает путь без языкового префикса.
 * /ru/analysis -> /analysis, /en/ -> /, /ru/share/xxx -> /share/xxx
 */
export function getPathWithoutLang(pathname: string): string {
  const m = pathname.match(LANG_PREFIX_REGEX);
  if (m) return m[2] === undefined ? '/' : m[2];
  return pathname;
}

/**
 * Извлекает язык из pathname. Возвращает 'ru' | 'en' или null.
 */
export function getLangFromPath(pathname: string): 'ru' | 'en' | null {
  const m = pathname.match(LANG_PREFIX_REGEX);
  return (m?.[1] as 'ru' | 'en') ?? null;
}

/**
 * Регулярное выражение для парсинга shareId из пути
 * Поддерживает формат: /share/{shareId} или /share/{shareId}-{slug}
 * shareId: 16 символов (a-zA-Z0-9)
 * slug: опционально, латинские буквы, цифры, дефисы
 */
export const SHARE_PATH_REGEX = /^\/share\/([a-zA-Z0-9]+(?:-[a-z0-9-]+)?)$/;

/**
 * Регулярное выражение для парсинга shareId из hash-роута
 * Поддерживает формат: #/share/{shareId} или #/share/{shareId}-{slug}
 */
export const SHARE_HASH_REGEX = /^#\/share\/([a-zA-Z0-9]+(?:-[a-z0-9-]+)?)$/;

/**
 * Извлекает shareId из пути URL
 * @param path - Путь URL (например, "/share/63a1bcf6e67e69ee-strah-oshibki")
 * @returns shareId со slug или null, если не найден
 */
export function extractShareIdFromPath(path: string): string | null {
  const match = path.match(SHARE_PATH_REGEX);
  return match ? match[1] : null;
}

/**
 * Извлекает shareId из hash-роута
 * @param hash - Hash часть URL (например, "#/share/63a1bcf6e67e69ee-strah-oshibki")
 * @returns shareId со slug или null, если не найден
 */
export function extractShareIdFromHash(hash: string): string | null {
  const match = hash.match(SHARE_HASH_REGEX);
  return match ? match[1] : null;
}

/**
 * Проверяет, является ли путь маршрутом share
 * @param path - Путь URL
 * @param hash - Hash часть URL (опционально)
 * @returns true, если это маршрут share
 */
export function isShareRoute(path: string, hash?: string): boolean {
  if (SHARE_PATH_REGEX.test(path)) {
    return true;
  }
  if (hash && SHARE_HASH_REGEX.test(hash)) {
    return true;
  }
  return false;
}

/**
 * Извлекает shareId из текущего URL (проверяет и path, и hash).
 * Учитывает языковой префикс в path: /ru/share/xxx -> shareId из /share/xxx
 * @returns shareId со slug или null, если не найден
 */
export function getShareIdFromUrl(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const path = window.location.pathname;
  const pathWithoutLang = getPathWithoutLang(path);
  const hash = window.location.hash;

  return extractShareIdFromPath(pathWithoutLang) || extractShareIdFromHash(hash);
}
